/**
 * Store vocabulary engine.
 *
 * A store's `offering_type` decides whether it is a *shop* (goods) or a
 * *practice* (appointments). Those are two different languages: a shop has
 * products, a cart and orders; a practice has services, bookings and a
 * schedule. Rather than scatter `offering_type === 'appointments' ? … : …`
 * across dozens of components, every customer- and owner-facing word lives
 * here, keyed by offering type.
 *
 * Usage:
 *   import { getVocab } from '../utils/storeVocabulary';
 *   const t = getVocab(store?.offering_type);
 *   <button>{t.addToCart}</button>     // "הוסף לסל" or "קביעת תור"
 *
 * The app is Hebrew-only today, so these are the Hebrew strings. When real i18n
 * arrives, this stays the single source of truth — only the values change.
 */

export const OFFERING_GOODS = 'goods';
export const OFFERING_APPOINTMENTS = 'appointments';
export const VALID_OFFERING_TYPES = [OFFERING_GOODS, OFFERING_APPOINTMENTS];
export const DEFAULT_OFFERING_TYPE = OFFERING_GOODS;

/** Coerce any input (undefined, legacy stores, junk) to a valid offering type. */
export const normalizeOfferingType = (value) =>
    VALID_OFFERING_TYPES.includes(value) ? value : DEFAULT_OFFERING_TYPE;

export const isAppointments = (value) => normalizeOfferingType(value) === OFFERING_APPOINTMENTS;

const VOCAB = {
    [OFFERING_GOODS]: {
        // Identity
        storeNoun: 'חנות',
        storeNounDefinite: 'החנות',
        createStoreTitle: 'יצירת חנות חדשה',

        // Catalog
        item: 'מוצר',
        items: 'מוצרים',
        catalog: 'מוצרים',
        catalogManage: 'ניהול מוצרים',
        addItem: 'הוסף מוצר',
        emptyCatalog: 'אין עדיין מוצרים',

        // Cart & purchase flow
        cart: 'סל הקניות',
        addToCart: 'הוסף לסל',
        emptyCart: 'הסל ריק',
        checkout: 'מעבר לתשלום',
        checkoutTitle: 'סיום הזמנה',
        placeOrder: 'שליחת הזמנה',
        primaryCta: 'הוסף לסל',

        // Orders (the transaction)
        order: 'הזמנה',
        orders: 'הזמנות',
        ordersManage: 'ניהול הזמנות',
        orderNumber: 'מספר הזמנה',
        emptyOrders: 'אין עדיין הזמנות',
        newOrderNotice: 'הזמנה חדשה התקבלה',

        // The person on the other side
        customer: 'לקוח',
        customers: 'לקוחות',

        // Time / fulfillment
        slotPickerTitle: 'בחירת מועד איסוף',
        scheduleTab: 'מועדי איסוף',

        // Owner dashboard
        dashboardOrdersTab: 'הזמנות',

        // Completion status label (retail: handed over)
        completedStatus: 'נמסר',
    },

    [OFFERING_APPOINTMENTS]: {
        // Identity — the container is still a "store" (חנות); only its contents
        // (services/appointments) differ. Avoids colliding with Mekomit's
        // separate "עסק" (business) concept.
        storeNoun: 'חנות',
        storeNounDefinite: 'החנות',
        createStoreTitle: 'יצירת חנות חדשה',

        // Catalog
        item: 'שירות',
        items: 'שירותים',
        catalog: 'שירותים',
        catalogManage: 'ניהול שירותים',
        addItem: 'הוסף שירות',
        emptyCatalog: 'אין עדיין שירותים',

        // Booking flow (no cart — book directly)
        cart: 'התורים שלי',
        addToCart: 'קביעת תור',
        emptyCart: 'לא נבחרו תורים',
        checkout: 'אישור התור',
        checkoutTitle: 'אישור קביעת תור',
        placeOrder: 'קביעת תור',
        primaryCta: 'קביעת תור',

        // Bookings (the transaction)
        order: 'תור',
        orders: 'תורים',
        ordersManage: 'ניהול תורים',
        orderNumber: 'מספר תור',
        emptyOrders: 'אין עדיין תורים',
        newOrderNotice: 'נקבע תור חדש',

        // The person on the other side
        customer: 'מטופל',
        customers: 'מטופלים',

        // Time / scheduling (the core of the experience)
        slotPickerTitle: 'בחירת מועד לתור',
        scheduleTab: 'יומן',

        // Owner dashboard
        dashboardOrdersTab: 'תורים',

        // Completion status label (practice: session happened)
        completedStatus: 'בוצע',
    },
};

/**
 * Resolve the vocabulary for an offering type. Always returns a complete map
 * (missing keys fall back to the goods vocabulary, so a partially-defined
 * appointments vocab can never render `undefined`).
 *
 * @param {string} offeringType - 'goods' | 'appointments' (or a store object).
 * @returns {Object} the resolved vocabulary
 */
export const getVocab = (offeringType) => {
    // Allow passing a store object directly for convenience.
    const type = typeof offeringType === 'object' && offeringType !== null
        ? offeringType.offering_type
        : offeringType;
    const resolved = normalizeOfferingType(type);
    return { ...VOCAB[OFFERING_GOODS], ...VOCAB[resolved] };
};

export default getVocab;
